import { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { Home, MessageSquare, HandHeart, DollarSign, Menu, Bell, UserRound, Settings, Gift } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { fileUrl } from "../lib/api";
import { openGive } from "../lib/give";
import InstallPrompt from "./InstallPrompt";
import NotificationBell from "./NotificationBell";
import TripSwitcher from "./TripSwitcher";
import { BookOpen } from "lucide-react";
import { initForegroundMessages, pushEnabled } from "../lib/push";
import { toast } from "sonner";

const navItems = [
  { to: "/app", icon: Home, label: "Home", end: true, id: "home" },
  { to: "/app/feed", icon: MessageSquare, label: "Feed", id: "feed" },
  { to: "/app/prayer", icon: HandHeart, label: "Prayer", id: "prayer" },
  { to: "/app/budget", icon: DollarSign, label: "Finances", id: "budget" },
  { to: "/app/more", icon: Menu, label: "More", id: "more" },
];

const partnerNavItems = [
  { to: "/app", icon: UserRound, label: "You", end: true, id: "home" },
  { to: "/app/feed", icon: MessageSquare, label: "Feed", id: "feed" },
  { to: "/app/prayer", icon: HandHeart, label: "Prayer", id: "prayer" },
  { to: "/app/budget", icon: DollarSign, label: "Finances", id: "budget" },
  { to: "/app/more", icon: Settings, label: "My Account", id: "more" },
];

export default function AppShell() {
  const { user, org, config, canViewFinancials, financialsUpsell, isLookingBack, isReadOnly, setViewTrip } = useAuth();
  const navigate = useNavigate();
  const isPartner = user?.role === "partner";
  const giveUrl = config?.donate_url || (org?.slug === "reset" ? "https://akop.live/give" : "");
  const [notif, setNotif] = useState(0);
  const [pushOff, setPushOff] = useState(false);
  useEffect(() => {
    const check = () => setPushOff(isPartner && !pushEnabled());
    check();
    window.addEventListener("focus", check);
    const t = setInterval(check, 4000);
    return () => { window.removeEventListener("focus", check); clearInterval(t); };
  }, [isPartner]);
  useEffect(() => {
    const navigate2 = navigate;
    initForegroundMessages((title, body, url) => {
      toast(title, { description: body, action: url ? { label: "View", onClick: () => navigate2(url.replace(window.location.origin, "")) } : undefined });
    });
  }, [navigate]);
  useEffect(() => {
    if (user?.role !== "admin") return;
    let active = true;
    const fetchN = async () => {
      try { const { data } = await api.get("/notifications"); if (active) setNotif(data.private_prayers || 0); } catch {}
    };
    fetchN();
    const t = setInterval(fetchN, 30000);
    return () => { active = false; clearInterval(t); };
  }, [user]);
  return (
    <div className="min-h-screen bg-cream">
      <header className={`sticky top-0 z-30 backdrop-blur-xl border-b ${isPartner ? "bg-[#0d2340] border-white/10" : "bg-[#FAF8F4]/90 border-[#2C2C3E]/5"}`} style={{ paddingTop: "env(safe-area-inset-top)" }}>
        <div className="mx-auto max-w-2xl px-5 py-3 flex items-center gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer" onClick={() => navigate("/app")} role="button" data-testid="app-header">
            {org?.logo_file_id ? (
              <img src={fileUrl(`/api/files/${org.logo_file_id}`, 200)} alt="logo" className="h-9 w-9 rounded-xl object-cover" />
            ) : (
              <div className={`h-9 w-9 rounded-xl flex items-center justify-center text-white font-heading font-bold ${isPartner ? "bg-[#E0A73A]" : "accent-bg"}`}>
                {(org?.org_name || "M")[0]}
              </div>
            )}
            <div className="leading-tight min-w-0">
              <div className={`font-heading font-bold text-lg truncate ${isPartner ? "text-white" : "text-[var(--brand-navy)]"}`}>{org?.org_name || "MissionCompanion"}</div>
              <div onClick={(e) => e.stopPropagation()}><TripSwitcher tone={isPartner ? "light" : "dark"} /></div>
            </div>
          </div>
          {isPartner && giveUrl && !isReadOnly && (
            <button
              data-testid="topbar-give-btn"
              onClick={() => openGive(giveUrl)}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-bold text-[#0d2340] shadow-sm transition-transform active:scale-95 hover:opacity-90"
              style={{ background: "#E0A73A" }}
              aria-label="Give to this mission"
            >
              <Gift className="h-4 w-4" /> Give
            </button>
          )}
          {(isPartner || user?.role === "admin") ? (
            <NotificationBell tone={isPartner ? "light" : "dark"} />
          ) : null}
        </div>
      </header>

      {isLookingBack && (
        <div data-testid="looking-back-banner" className="sticky z-20 text-white text-xs" style={{ top: "calc(env(safe-area-inset-top) + 61px)", background: isReadOnly ? "#3b4a63" : "#2563eb" }}>
          <div className="mx-auto max-w-2xl px-5 py-2 flex items-center gap-2">
            <BookOpen className="h-3.5 w-3.5 shrink-0" />
            <span className="flex-1 min-w-0 truncate">
              {isReadOnly ? <>Looking back at <b>{config?.trip_name}</b> · read-only</> : <>Previewing <b>{config?.trip_name}</b> · {config?.status === "planning" ? "hidden from partners" : config?.status}</>}
            </span>
            <button data-testid="looking-back-return" onClick={() => setViewTrip(null)} className="shrink-0 rounded-full bg-white/15 hover:bg-white/25 px-2.5 py-1 font-semibold">Back to current ↩</button>
          </div>
        </div>
      )}

      <main className="mx-auto max-w-2xl px-4 pt-4 pb-28">
        <Outlet />
      </main>

      <InstallPrompt />

      <nav className="fixed bottom-0 inset-x-0 z-30 backdrop-blur-xl bg-white/95 border-t border-[#2C2C3E]/5" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="mx-auto max-w-2xl px-2 flex justify-around">
          {(isPartner ? partnerNavItems : navItems).filter((it) => it.id !== "budget" || canViewFinancials() || financialsUpsell()).map((it) => {
            const Icon = it.icon;
            return (
              <NavLink
                key={it.id}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.id}`}
                className={({ isActive }) =>
                  `flex flex-col items-center gap-1 py-3 px-3 min-w-[60px] transition-colors ${isActive ? "accent-text" : "text-muted-foreground"}`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative">
                      <Icon className="h-6 w-6" strokeWidth={isActive ? 2.4 : 1.9} />
                      {it.id === "more" && pushOff && (
                        <span data-testid="push-nudge-dot" className="absolute -top-0.5 -right-1 h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white" />
                      )}
                    </span>
                    <span className="text-xs font-medium">{it.label}</span>
                  </>
                )}
              </NavLink>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
